/**
 * Honest-Scorecard "Send to a colleague" share button — CRM lead-gen.
 *
 * Visitor clicks → modal opens → enters their email + optional recipient
 * email → we POST to /api/scorecard-share/generate (rate-limited 1/hour
 * per sender). Returns a tracked URL + optional Resend email send.
 *
 * Anti-spam: rate-limit enforced server-side; client gets a 429 with retry-after.
 */
import React, { useState } from 'react';
import { Send, Copy, CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ScorecardShareButton() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    sender_email: '', recipient_email: '', recipient_name: '', custom_note: '',
  });

  const isValidEmail = (e) => /\S+@\S+\.\S+/.test(e);
  const canSubmit = isValidEmail(form.sender_email) && (!form.recipient_email || isValidEmail(form.recipient_email));

  const submit = async (mode) => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const endpoint = mode === 'email' ? '/api/scorecard-share/send-email' : '/api/scorecard-share/generate';
      const body = mode === 'email'
        ? { sender_email: form.sender_email, recipient_email: form.recipient_email,
             recipient_name: form.recipient_name || null, custom_note: form.custom_note || null }
        : { sender_email: form.sender_email };
      const r = await fetch(`${API}${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data.detail || data.error || `HTTP ${r.status}`);
      } else {
        setResult({ ...data, mode });
      }
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!result?.shareable_url) return;
    try {
      await navigator.clipboard.writeText(result.shareable_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked */ }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/30 text-cyan-200 font-medium px-5 py-3 rounded-full transition-colors text-sm"
        data-testid="scorecard-share-open-btn"
      >
        <Send className="w-4 h-4" /> Send to a colleague
      </button>

      {open && (
        <div className="fixed inset-0 bg-slate-950 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="scorecard-share-modal">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-lg w-full overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <h3 className="font-bold text-white">Share the scorecard</h3>
              <button onClick={() => { setOpen(false); setResult(null); setError(null); }} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              {!result ? (
                <>
                  <p className="text-sm text-slate-400 mb-4">Send a colleague your tracked link to the CARTIE scorecard. We'll email it for you, or you can copy the URL.</p>
                  <div className="space-y-3">
                    <input
                      type="email" placeholder="Your email" value={form.sender_email}
                      onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
                      data-testid="share-sender-input"
                    />
                    <input
                      type="email" placeholder="Recipient email (optional)" value={form.recipient_email}
                      onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
                      data-testid="share-recipient-input"
                    />
                    {form.recipient_email && (
                      <>
                        <input
                          type="text" placeholder="Recipient name (optional)" value={form.recipient_name}
                          onChange={(e) => setForm({ ...form, recipient_name: e.target.value })}
                          className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
                        />
                        <textarea
                          placeholder="Custom note (optional)" value={form.custom_note}
                          onChange={(e) => setForm({ ...form, custom_note: e.target.value })}
                          rows={2}
                          className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
                        />
                      </>
                    )}
                  </div>
                  {error && (
                    <div className="mt-3 px-3 py-2 rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs flex items-center gap-1.5" data-testid="share-error">
                      <AlertTriangle className="w-3 h-3" /> {error}
                    </div>
                  )}
                  <div className="flex gap-2 mt-4">
                    <Button
                      onClick={() => submit('url')} disabled={!canSubmit || busy}
                      className="flex-1 bg-cyan-600 hover:bg-cyan-500 text-white"
                      data-testid="share-copy-link-btn"
                    >
                      Copy link
                    </Button>
                    {form.recipient_email && (
                      <Button
                        onClick={() => submit('email')} disabled={!canSubmit || busy}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
                        data-testid="share-send-email-btn"
                      >
                        {busy ? 'Sending…' : 'Send email'}
                      </Button>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-3 italic">Rate-limited: 1 share per email per hour to prevent spam.</p>
                </>
              ) : (
                <div className="space-y-4" data-testid="share-result">
                  <div className="flex items-center gap-2 text-emerald-300">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-bold">
                      {result.mode === 'email' && result.email_sent ? 'Email sent!' :
                       result.mode === 'email' ? 'Link generated (email pending)' : 'Tracked link ready'}
                    </span>
                  </div>
                  {result.shareable_url && (
                    <>
                      <div className="bg-slate-950 border border-slate-700 rounded-md p-3 text-xs text-emerald-300 break-all font-mono">{result.shareable_url}</div>
                      <button onClick={copy} className="inline-flex items-center gap-1.5 text-sm text-cyan-300 hover:text-cyan-200">
                        {copied ? <><CheckCircle2 className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy URL</>}
                      </button>
                    </>
                  )}
                  {result.email_error && (
                    <div className="text-xs text-amber-300">Email send failed: {result.email_error}. URL above still works.</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
